import { env } from './env';
import { logger } from './logger';
import { supabase, isPreviewMode } from './supabase';
import type { VisionItem, VisionAnalysis } from '../types';
import { withRetry } from '../utils/retry';
import { circuitBreakers } from '../utils/circuit-breaker';
import { modelConfig } from './model-config';

interface EdgeFunctionResponse {
  content: string;
  error?: string;
}

async function callEdgeFunction(messages: Array<{ role: string; content: string }>, maxTokens = 1000): Promise<string> {
  const edgeFunctionUrl = `${env.VITE_SUPABASE_URL}/functions/v1/ai-coach-chat`;

  const response = await fetch(edgeFunctionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      messages,
      model: modelConfig.getModelName(),
      temperature: 0.7,
      max_tokens: maxTokens
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  const data: EdgeFunctionResponse = await response.json();
  if (data.error) {
    throw new Error(data.error);
  }

  return data.content;
}

const FALLBACK_IMAGES = {
  career: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab',
  personal: 'https://images.unsplash.com/photo-1490730141103-6cac27aaab94',
  health: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b',
  relationships: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18',
  finance: 'https://images.unsplash.com/photo-1579621970588-a35d0e7ab9b6',
  manifestation: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc',
  practices: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773',
  technology: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40',
  success: 'https://images.unsplash.com/photo-1552664730-d307ca884978',
  mindfulness: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773',
  spirituality: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc'
};

function getCategoryFromPrompt(prompt: string): keyof typeof FALLBACK_IMAGES {
  const promptLower = prompt.toLowerCase();

  if (promptLower.includes('career') || promptLower.includes('business') || promptLower.includes('work')) {
    return 'career';
  } else if (promptLower.includes('health') || promptLower.includes('fitness') || promptLower.includes('wellness')) {
    return 'health';
  } else if (promptLower.includes('relationship') || promptLower.includes('love') || promptLower.includes('family')) {
    return 'relationships';
  } else if (promptLower.includes('money') || promptLower.includes('finance') || promptLower.includes('wealth')) {
    return 'finance';
  } else if (promptLower.includes('manifestation') || promptLower.includes('manifest')) {
    return 'manifestation';
  } else if (promptLower.includes('practice') || promptLower.includes('ritual') || promptLower.includes('routine')) {
    return 'practices';
  } else if (promptLower.includes('tech') || promptLower.includes('digital') || promptLower.includes('ai')) {
    return 'technology';
  } else if (promptLower.includes('success') || promptLower.includes('achievement')) {
    return 'success';
  } else if (promptLower.includes('mindful') || promptLower.includes('meditation')) {
    return 'mindfulness';
  } else if (promptLower.includes('spirit') || promptLower.includes('soul') || promptLower.includes('energy')) {
    return 'spirituality';
  }
  return 'personal';
}

export async function generateImage(prompt: string): Promise<string> {
  const category = getCategoryFromPrompt(prompt);
  logger.info('Using stock image for category:', category);
  return FALLBACK_IMAGES[category];
}

export async function analyzeVisionBoard(items: VisionItem[]): Promise<VisionAnalysis> {
  try {
    if (isPreviewMode()) {
      return {
        insights: ["Your vision board shows clear focus on what matters most to you."],
        energyScore: 85,
        manifestationProbability: 90,
        suggestions: ["Review your goals daily to stay focused and motivated."]
      };
    }

    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return getDefaultVisionAnalysis();
    }

    const prompt = `As a life coach, analyze this vision board with ${items.length} items. Provide practical insights about their goals, priorities, and what these choices reveal about their ambitions.

    Vision Board Items:
    ${items.map(item => `
      - Title: ${item.title}
      - Description: ${item.description}
      - Category: ${item.category}
    `).join('\n')}

    Respond in this exact format:
    INSIGHTS:
    1. [What their goals reveal about their priorities and ambitions]
    2. [Patterns you notice - are goals balanced or heavily weighted in one area?]
    3. [Opportunities for growth or gaps they might want to address]

    ENERGY SCORE: [0-100 based on clarity and actionability of goals]
    ACHIEVEMENT LIKELIHOOD: [0-100 based on how specific and realistic the goals are]

    SUGGESTIONS:
    1. [First specific, actionable step they could take this week]
    2. [Second practical suggestion to move toward their goals]
    3. [Third recommendation for building momentum]`;

    const content = await circuitBreakers.openai.execute(async () => {
      return await withRetry(
        async () => callEdgeFunction([
          {
            role: "system",
            content: "You are a supportive life coach analyzing goals for men aged 16-40. Be encouraging but practical. Focus on actionable advice, not abstract concepts. Celebrate ambition while helping them think realistically about execution. Your tone should be motivational like a mentor who genuinely wants them to succeed."
          },
          {
            role: "user",
            content: prompt
          }
        ], 1000),
        {
          maxAttempts: 2,
          backoffMs: 1000,
          retryableErrors: ['timeout', 'network', 'fetch failed'],
          onRetry: (attempt, error) => {
            logger.warn(`Retry attempt ${attempt} for vision board analysis:`, error);
          }
        }
      );
    });

    const insightsMatch = content.match(/INSIGHTS:\n((?:^\d+\.\s*.*\n?)*)/m);
    const insights = insightsMatch
      ? insightsMatch[1]
          .split('\n')
          .map(s => s.replace(/^\d+\.\s*/, '').trim())
          .filter(s => s)
      : [];

    const energyMatch = content.match(/ENERGY SCORE:\s*(\d+)/);
    const energyScore = energyMatch
      ? parseInt(energyMatch[1])
      : Math.floor(Math.random() * 20) + 80;

    const achievementMatch = content.match(/ACHIEVEMENT LIKELIHOOD:\s*(\d+)/);
    const manifestationProbability = achievementMatch
      ? parseInt(achievementMatch[1])
      : Math.floor(Math.random() * 15) + 85;

    const suggestionsMatch = content.match(/SUGGESTIONS:\n((?:^\d+\.\s*.*\n?)*)/m);
    const suggestions = suggestionsMatch
      ? suggestionsMatch[1]
          .split('\n')
          .map(s => s.replace(/^\d+\.\s*/, '').trim())
          .filter(s => s)
      : [];

    return {
      insights,
      energyScore,
      manifestationProbability,
      suggestions
    };
  } catch (error: any) {
    logger.error('Vision board analysis failed:', error);
    return getDefaultVisionAnalysis();
  }
}

function getDefaultVisionAnalysis(): VisionAnalysis {
  return {
    insights: [
      "Your vision board shows you're thinking seriously about your future.",
      "You've got a mix of goals across different areas of life.",
      "Having visual reminders of your goals helps maintain focus."
    ],
    energyScore: 85,
    manifestationProbability: 90,
    suggestions: [
      "Pick one goal and identify the very next action you need to take.",
      "Set a weekly check-in to review progress on your top priorities.",
      "Consider adding deadlines to make your goals more concrete."
    ]
  };
}

interface JournalAnalysisInput {
  quickEntry?: string;
  winOfDay?: string;
  challengeFaced?: string;
  whatWorked?: string;
  whatToImprove?: string;
  energyLevel?: number;
  mood?: string;
  gratitude?: string;
  intentions?: string;
  manifestations?: string;
  reflections?: string;
}

export async function analyzeJournalEntry(entry: JournalAnalysisInput): Promise<{
  insights: string[];
  energyScore: number;
  recommendations: string[];
}> {
  const entryContent = [
    entry.quickEntry,
    entry.winOfDay && `Win: ${entry.winOfDay}`,
    entry.challengeFaced && `Challenge: ${entry.challengeFaced}`,
    entry.whatWorked && `What worked: ${entry.whatWorked}`,
    entry.whatToImprove && `What to improve: ${entry.whatToImprove}`,
    entry.gratitude && `Gratitude: ${entry.gratitude}`,
    entry.intentions && `Intentions: ${entry.intentions}`,
    entry.reflections && `Reflections: ${entry.reflections}`
  ].filter(Boolean).join('\n');

  if (!entryContent.trim()) {
    return {
      insights: ["Share more about your day to get personalized insights."],
      energyScore: 50,
      recommendations: ["Try writing about what's on your mind right now."]
    };
  }

  try {
    if (isPreviewMode()) {
      return generateContextualFallback(entryContent, entry.mood);
    }

    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      return generateContextualFallback(entryContent, entry.mood);
    }

    const prompt = `Analyze this journal entry and provide specific, actionable feedback:

Entry: "${entryContent}"
${entry.mood ? `Mood: ${entry.mood}` : ''}
${entry.energyLevel ? `Energy Level: ${entry.energyLevel}/10` : ''}

Based on what they wrote, provide:

INSIGHTS:
1. [Specific observation about what they shared - reference their actual words]
2. [Pattern or theme you notice in their entry]
3. [Acknowledge their self-awareness or honesty]

RECOMMENDATIONS:
1. [One specific, actionable step they could take today]
2. [A practical suggestion related to what they wrote about]
3. [Something to consider or reflect on]

ENERGY SCORE: [0-100 based on the entry's tone and content]`;

    const content = await callEdgeFunction([
      {
        role: "system",
        content: "You are a supportive life coach for men aged 16-40. Analyze their journal entry and give feedback that directly addresses what they wrote. Be specific - reference their actual words. Acknowledge difficult feelings like stress honestly. Give practical suggestions, not generic advice. Keep it real and grounded. Never use spiritual or manifestation language."
      },
      {
        role: "user",
        content: prompt
      }
    ], 600);

    const insightsMatch = content.match(/INSIGHTS:\n((?:^\d+\.\s*.*\n?)*)/m);
    const insights = insightsMatch
      ? insightsMatch[1]
          .split('\n')
          .map(s => s.replace(/^\d+\.\s*/, '').trim())
          .filter(s => s)
      : [];

    const recommendationsMatch = content.match(/RECOMMENDATIONS:\n((?:^\d+\.\s*.*\n?)*)/m);
    const recommendations = recommendationsMatch
      ? recommendationsMatch[1]
          .split('\n')
          .map(s => s.replace(/^\d+\.\s*/, '').trim())
          .filter(s => s)
      : [];

    const energyMatch = content.match(/ENERGY SCORE:\s*(\d+)/);
    const energyScore = energyMatch
      ? parseInt(energyMatch[1])
      : estimateEnergyFromContent(entryContent, entry.mood);

    if (insights.length === 0 && recommendations.length === 0) {
      return generateContextualFallback(entryContent, entry.mood);
    }

    return {
      insights: insights.length > 0 ? insights : ["Thanks for sharing what's on your mind."],
      recommendations: recommendations.length > 0 ? recommendations : ["Take it one step at a time."],
      energyScore
    };
  } catch (error: any) {
    logger.error('Journal analysis failed:', error);
    return generateContextualFallback(entryContent, entry.mood);
  }
}

function estimateEnergyFromContent(content: string, mood?: string): number {
  const lowerContent = content.toLowerCase();
  const stressWords = ['stressed', 'anxious', 'worried', 'overwhelmed', 'tired', 'exhausted', 'frustrated', 'angry', 'sad', 'depressed'];
  const positiveWords = ['good', 'great', 'happy', 'excited', 'accomplished', 'proud', 'energized', 'motivated', 'grateful', 'win'];

  let score = 50;
  stressWords.forEach(word => { if (lowerContent.includes(word)) score -= 8; });
  positiveWords.forEach(word => { if (lowerContent.includes(word)) score += 8; });

  if (mood === 'stressed' || mood === 'anxious') score -= 15;
  if (mood === 'joyful' || mood === 'peaceful') score += 15;

  return Math.max(20, Math.min(95, score));
}

function generateContextualFallback(content: string, mood?: string): {
  insights: string[];
  energyScore: number;
  recommendations: string[];
} {
  const lowerContent = content.toLowerCase();
  const insights: string[] = [];
  const recommendations: string[] = [];

  if (lowerContent.includes('stress') || lowerContent.includes('overwhelm') || mood === 'stressed') {
    insights.push("Sounds like you're dealing with a lot right now.");
    insights.push("Recognizing when you're stressed is an important first step.");
    recommendations.push("Try breaking down what's causing the stress into smaller pieces.");
    recommendations.push("What's one thing you can control or do something about today?");
  } else if (lowerContent.includes('christmas') || lowerContent.includes('holiday')) {
    insights.push("Holiday prep can be demanding - you're not alone in feeling this way.");
    recommendations.push("Consider what's actually essential vs. what you can skip or simplify.");
    recommendations.push("Build in some downtime for yourself, even 15 minutes.");
  } else if (lowerContent.includes('tired') || lowerContent.includes('exhausted')) {
    insights.push("Your body might be telling you something important.");
    recommendations.push("Are you getting enough sleep? That's usually the first place to look.");
    recommendations.push("Consider what's draining your energy and if anything can be adjusted.");
  } else {
    insights.push("Thanks for checking in with yourself today.");
    insights.push("Writing things down helps create clarity.");
    recommendations.push("What's the most important thing for you to focus on right now?");
  }

  if (insights.length < 3) {
    insights.push("Showing up to journal, even briefly, is a solid habit.");
  }
  if (recommendations.length < 3) {
    recommendations.push("Keep logging - patterns become clearer over time.");
  }

  return {
    insights: insights.slice(0, 3),
    energyScore: estimateEnergyFromContent(content, mood),
    recommendations: recommendations.slice(0, 3)
  };
}

export async function getAICoachResponse(message: string, customSystemPrompt?: string): Promise<string> {
  try {
    if (isPreviewMode()) {
      return `I hear you, and I'm here to support you. Let's start with one simple question: What feels like the right next step for you? Even something small counts. What do you think you could handle today?`;
    }

    const defaultSystemPrompt = "You are a supportive life coach and accountability partner for men aged 16-40. Your approach is motivational and encouraging with occasional directness when needed. Help them think critically by asking questions that lead to self-discovery. Be warm and genuine, like a mentor who truly cares about their growth. Celebrate progress, acknowledge struggles with empathy, and guide them to find their own solutions. Your goal is to build their confidence and independence so they can eventually coach themselves. Keep responses under 150 words.";

    const content = await callEdgeFunction([
      {
        role: "system",
        content: customSystemPrompt || defaultSystemPrompt
      },
      {
        role: "user",
        content: message
      }
    ], 300);

    return content || "I'm here to help you take action. What are you working on or stuck with?";
  } catch (error: any) {
    logger.error('AI coach response error:', error);
    return "Having a technical issue. Try again. In the meantime: what's one action you can take right now?";
  }
}
